import { Controller } from "@hotwired/stimulus";
import TomSelect from "tom-select";

export default class extends Controller {
  static targets = ["itemFilter", "versionFilter", "selectElement"];
  static values = {
    enableItemFilter: { type: Boolean, default: true },
    enableVersionFilter: { type: Boolean, default: true },

    itemsUrl: {
      type: String,
      default: "/development/filter_data/software_items",
    },
    versionsUrl: {
      type: String,
      default: "/development/filter_data/software_versions",
    },
    filterUrl: {
      type: String,
      default: "/development/filter_data/software_requirements",
    },
    placeholder: { type: String, default: "Select items..." },

    singleVersionPerRequirement: { type: Boolean, default: false },
  };

  badgeColors = [
    "bg-primary",
    "bg-secondary",
    "bg-success",
    "bg-danger",
    "bg-warning",
    "bg-info",
    "bg-purple",
    "bg-pink",
    "bg-orange",
    "bg-teal",
  ];

  async connect() {
    if (!this.hasSelectElementTarget) {
      console.error(
        "SoftwareRequirementsSelectController: Could not find selectElement target within the controller scope!",
      );
      return;
    }

    await this.createFilters();
    await this.loadInitialOptions();
    this.initializeTomSelect();

    if (this.enableItemFilterValue || this.enableVersionFilterValue) {
      this.updateOptions();
    }
  }

  async loadInitialOptions() {
    const url = new URL(this.filterUrlValue, window.location.origin);
    try {
      const response = await fetch(url);
      if (!response.ok)
        throw new Error(`Failed to fetch requirements: ${response.statusText}`);
      this.initialOptions = await response.json();
    } catch (error) {
      console.error(
        "SoftwareRequirementsSelectController: Error loading initial options:",
        error,
      );
      this.initialOptions = [];
    }
  }

  disconnect() {
    if (this.select) {
      this.select.destroy();
      this.select = null;
    }
    this.element
      .querySelector(".software-requirement-filters-wrapper")
      ?.remove();
  }

  async createFilters() {
    const filterPromises = [];

    if (!this.enableItemFilterValue && !this.enableVersionFilterValue) {
      return;
    }

    const labelElement = this.element.querySelector("label");
    let insertBeforeElement = this.selectElementTarget;
    if (labelElement) {
      insertBeforeElement = labelElement.nextSibling;
    } else {
      console.warn(
        "SoftwareRequirementsSelectController: Could not find label within the controller scope. Inserting filters before select element.",
      );
    }

    let filterWrapper = this.element.querySelector(
      ".software-requirement-filters-wrapper",
    );
    if (!filterWrapper) {
      filterWrapper = this._createFilterWrapper(insertBeforeElement);
      if (!filterWrapper) return;
    }
    filterWrapper.innerHTML = "";
    filterWrapper.classList.add("d-inline-block", "ms-2");

    if (this.enableItemFilterValue) {
      filterPromises.push(this.createItemFilter(filterWrapper));
    }
    if (this.enableVersionFilterValue) {
      filterPromises.push(this.createVersionFilter(filterWrapper));
    }
    await Promise.all(filterPromises);
  }

  createItemFilter(container) {
    return this._createFilterSelect({
      container: container,
      urlValue: this.itemsUrlValue,
      targetName: "itemFilter",
      defaultOptionText: " All Software Items ",
    });
  }

  createVersionFilter(container) {
    return this._createFilterSelect({
      container: container,
      urlValue: this.versionsUrlValue,
      targetName: "versionFilter",
      defaultOptionText: " All Software Versions ",
    });
  }

  initializeTomSelect() {
    if (this.select) {
      this.select.destroy();
    }
    if (!this.hasSelectElementTarget) {
      console.error(
        "SoftwareRequirementsSelectController: Cannot initialize TomSelect: selectElement target not found.",
      );
      return;
    }

    const badgeColors = this.badgeColors;
    const colorCount = badgeColors.length;

    const initialValues = Array.from(
      this.selectElementTarget.selectedOptions,
    ).map((option) => option.value);

    const options = {
      plugins: { remove_button: { title: "Remove this item" } },
      placeholder:
        this.selectElementTarget.dataset.placeholder || this.placeholderValue,
      options: this.initialOptions || [],
      valueField: "id",
      labelField: "name",
      searchField: ["name", "software_items.name"],
      maxItems: null,
      create: false,
      items: initialValues,
      onItemAdd: (value) => this.dropOtherVersionsOfSameRequirement(value),

      render: {
        option: function (data, escape) {
          let badgesHtml = "";
          if (data.software_items && Array.isArray(data.software_items)) {
            badgesHtml = data.software_items
              .map((item) => {
                if (!item || typeof item.id === "undefined" || !item.name)
                  return "";
                const colorIndex = item.id % colorCount;
                const badgeClass = badgeColors[colorIndex] || "bg-secondary";
                const itemName = escape(item.name);

                return `<span class="badge ${badgeClass} text-white ms-1" title="Item: ${itemName}">${itemName}</span>`;
              })
              .join("");
          }
          return `<div>${escape(data.name)}${badgesHtml}</div>`;
        },
        item: function (data, escape) {
          let badgesHtml = "";
          if (data.software_items && Array.isArray(data.software_items)) {
            badgesHtml = data.software_items
              .map((item) => {
                if (!item || typeof item.id === "undefined" || !item.name)
                  return "";
                const colorIndex = item.id % colorCount;
                const badgeClass = badgeColors[colorIndex] || "bg-secondary";
                const itemName = escape(item.name);

                return `<span class="badge ${badgeClass} text-white ms-1" title="Item: ${itemName}">${itemName}</span>`;
              })
              .join("");
          }
          return `<div>${escape(data.name)}${badgesHtml}</div>`;
        },
      },
    };

    try {
      this.select = new TomSelect(this.selectElementTarget, options);
      this.select.setValue(initialValues, true);
    } catch (error) {
      console.error("Error creating TomSelect instance:", error);
    }
  }

  dropOtherVersionsOfSameRequirement(addedValue) {
    if (!this.singleVersionPerRequirementValue || !this.select) return;

    const requirementId =
      this.select.options[addedValue]?.software_requirement_id;
    if (!requirementId) return;

    this.select
      .getValue()
      .filter(
        (value) =>
          value !== addedValue &&
          this.select.options[value]?.software_requirement_id === requirementId,
      )
      .forEach((value) => this.select.removeItem(value, true));
  }

  async updateOptions() {
    if (!this.hasFilterUrlValue) {
      console.error(
        "SoftwareRequirementsSelectController: filterUrlValue is missing.",
      );
      return;
    }
    if (!this.select) {
      return;
    }

    const currentValues = this.select.getValue().map(String);

    const itemId = this.itemFilterTarget?.value || null;
    const versionId = this.versionFilterTarget?.value || null;

    const url = new URL(this.filterUrlValue, window.location.origin);
    if (itemId) url.searchParams.append("item_id", itemId);
    if (versionId) url.searchParams.append("version_id", versionId);

    try {
      const response = await fetch(url);
      if (!response.ok)
        throw new Error(
          `Failed to fetch filtered requirements: ${response.statusText}`,
        );
      let requirements = await response.json();

      currentValues.forEach((val) => {
        if (!requirements.find((r) => r.id.toString() === val)) {
          const original = (this.initialOptions || []).find(
            (r) => r.id.toString() === val,
          );
          if (original) requirements.push(original);
        }
      });

      this.select.clearOptions();
      this.select.addOptions(requirements);
      this.select.setValue(currentValues, true);
      this.select.refreshOptions(false);
    } catch (error) {
      console.error(
        "SoftwareRequirementsSelectController: Error updating options:",
        error,
      );
      this.select.clearOptions();
      this.select.addOption({ id: "", name: "Error loading requirements" });
      this.select.disable();
    }
  }

  _createFilterWrapper(insertBeforeElement) {
    const filterWrapper = document.createElement("div");
    filterWrapper.classList.add("software-requirement-filters-wrapper");
    try {
      this.element.insertBefore(filterWrapper, insertBeforeElement);
      return filterWrapper;
    } catch (error) {
      console.error(
        "SoftwareRequirementsSelectController: Error inserting filter wrapper:",
        error,
      );
      return null;
    }
  }

  async _createFilterSelect({
    container,
    urlValue,
    targetName,
    defaultOptionText,
  }) {
    if (!urlValue) {
      console.error(
        `SoftwareRequirementsSelectController: URL value is missing for ${targetName}.`,
      );
      return;
    }
    try {
      const response = await fetch(urlValue);
      if (!response.ok)
        throw new Error(
          `Failed to fetch data for ${targetName}: ${response.statusText}`,
        );
      const data = await response.json();

      const select = document.createElement("select");
      select.dataset.softwareRequirementsSelectTarget = targetName;
      select.dataset.action =
        "change->software-requirements-select#updateOptions";
      select.classList.add(
        "form-select",
        "form-select-sm",
        "d-inline-block",
        "me-2",
      );
      select.style.width = "auto";

      const defaultOption = document.createElement("option");
      defaultOption.value = "";
      defaultOption.text = defaultOptionText;
      select.appendChild(defaultOption);

      data.forEach((item) => {
        const option = document.createElement("option");
        option.value = item.id;
        option.text = item.name;
        select.appendChild(option);
      });

      container.appendChild(select);
    } catch (error) {
      console.error(
        `SoftwareRequirementsSelectController: Error creating filter ${targetName}:`,
        error,
      );
    }
  }
}
